import { Injectable } from '@angular/core';
import { Project } from '../model/project/project';
import { OfflineStorageTimestampService } from './offline-storage-timestamp.service';
import { CollectionHelper } from '../helpers/collection-helper';
import * as semaphore from 'semaphore';
import SemaphoreHelper from '../helpers/semaphore-helper';
import { Observable } from 'rxjs';
import { RequestService } from './request.service';
import { ConnectionStatusService } from './connection-status.service';
import RegisteredAction from '../model/registered-action';

@Injectable({
  providedIn: 'root',
})
export class OfflineProjectStorageService {
  private getFromOnline: boolean = true;
  static readonly storageKeyPrefix = 'project-';
  static readonly timestampCollectionKey = 'projects';
  static readonly offlineStorageMutex = semaphore(1);

  private expiredProjectRemover: ExpiredProjectRemover;

  constructor(
    private requestService: RequestService,
    private connectionStatusService: ConnectionStatusService,
    private offlineStorageTimestampService: OfflineStorageTimestampService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        this.getFromOnline = online;
      });
    this.expiredProjectRemover = new ExpiredProjectRemover(
      this.offlineStorageTimestampService
    );
    this.expiredProjectRemover.removeExpiredItems();
  }

  static getProjectStorageKey(id: number) {
    if (id == null) {
      throw new Error('id must not be null');
    }
    return this.storageKeyPrefix + id;
  }

  public getProject(id: number): Observable<Project> {
    if (id == null) {
      throw new Error('id must not be null');
    }
    return this.getFromOnline
      ? this.getProjectFromOnline(id)
      : this.getProjectFromOffline(id);
  }

  private getProjectFromOnline(id: number): Observable<Project> {
    const toReturn = this.requestService.getNormal(`/projects/${id}`);
    toReturn.subscribe((res) => {
      if (res != null && res.body != null) {
        SemaphoreHelper.use(
          OfflineProjectStorageService.offlineStorageMutex,
          async () => {
            localStorage.setItem(
              OfflineProjectStorageService.getProjectStorageKey(id),
              res
            );
            this.offlineStorageTimestampService.setTimestamps(
              OfflineProjectStorageService.timestampCollectionKey,
              [{ key: id.toString(), timestamp: Date.now() }]
            );
          }
        );
      }
    });
    return toReturn;
  }

  private getProjectFromOffline(id: number): Observable<Project> {
    const stringified = localStorage.getItem(
      OfflineProjectStorageService.getProjectStorageKey(id)
    );
    if (stringified != null) {
      SemaphoreHelper.use(
        OfflineProjectStorageService.offlineStorageMutex,
        async () => {
          this.offlineStorageTimestampService.setTimestamps(
            OfflineProjectStorageService.timestampCollectionKey,
            [{ key: id.toString(), timestamp: Date.now() }]
          );
        }
      );
    }
    return new Observable((subscriber) => {
      subscriber.next(stringified == null ? null : JSON.parse(stringified));
      subscriber.complete();
    });
  }

  public getStoredProject(id: number): Project {
    const stringified = localStorage.getItem(
      OfflineProjectStorageService.getProjectStorageKey(id)
    );
    if (stringified != null) {
      SemaphoreHelper.use(
        OfflineProjectStorageService.offlineStorageMutex,
        async () => {
          this.offlineStorageTimestampService.setTimestamps(
            OfflineProjectStorageService.timestampCollectionKey,
            [{ key: id.toString(), timestamp: Date.now() }]
          );
        }
      );
    }
    return stringified == null ? null : JSON.parse(stringified);
  }

  public storeProject(project: Project) {
    if (project == null || project.id == null) {
      throw new Error('project must not be null and id must not be null');
    }
    SemaphoreHelper.use(
      OfflineProjectStorageService.offlineStorageMutex,
      async () => {
        localStorage.setItem(
          OfflineProjectStorageService.getProjectStorageKey(project.id),
          JSON.stringify(project)
        );
        this.offlineStorageTimestampService.setTimestamps(
          OfflineProjectStorageService.timestampCollectionKey,
          [{ key: project.id.toString(), timestamp: Date.now() }]
        );
      }
    );
  }

  public storeProjects(projects: Project[]) {
    if (projects == null || projects.length === 0) {
      throw new Error('projects must not be null or empty');
    }
    if (projects.some((p) => p == null || p.id == null)) {
      throw new Error('none of the projects may be null or have null as id');
    }

    SemaphoreHelper.use(
      OfflineProjectStorageService.offlineStorageMutex,
      async () => {
        projects.forEach((p) => {
          localStorage.setItem(
            OfflineProjectStorageService.getProjectStorageKey(p.id),
            JSON.stringify(p)
          );
        });
        const now = Date.now();
        this.offlineStorageTimestampService.setTimestamps(
          OfflineProjectStorageService.timestampCollectionKey,
          projects.map((p) => {
            return { key: p.id.toString(), timestamp: now };
          })
        );
      }
    );
  }

  public async storeRegisteredActions(
    projectId: number,
    subProjectId: number,
    registeredActions: RegisteredAction[]
  ) {
    if (projectId == null) {
      throw new Error('projectId must not be null');
    }
    if (subProjectId == null) {
      throw new Error('subProjectId must not be null');
    }
    if (registeredActions == null) {
      throw new Error('registeredActions must not be null');
    }

    await SemaphoreHelper.use(
      OfflineProjectStorageService.offlineStorageMutex,
      async () => {
        const stringified = localStorage.getItem(
          OfflineProjectStorageService.getProjectStorageKey(projectId)
        );
        if (stringified != null) {
          const project = <Project>JSON.parse(stringified);
          const storedSubProject = CollectionHelper.isPopulated(
            project.subProjects
          )
            ? project.subProjects.find((sp) => sp.id === subProjectId)
            : null;
          if (
            storedSubProject != null &&
            storedSubProject.projectPlacement != null
          ) {
            storedSubProject.projectPlacement.registeredActions =
              registeredActions;
          }

          localStorage.setItem(
            OfflineProjectStorageService.getProjectStorageKey(projectId),
            JSON.stringify(project)
          );

          const now = Date.now();
          this.offlineStorageTimestampService.setTimestamps(
            OfflineProjectStorageService.timestampCollectionKey,
            [{ key: projectId.toString(), timestamp: now }]
          );
        }
      }
    );
  }
}

class ExpiredProjectRemover {
  private readonly expiredItemRemovalInterval = 1800000; // 30 minutes

  constructor(
    private offlineStorageTimestampService: OfflineStorageTimestampService
  ) {}

  removeExpiredItems() {
    SemaphoreHelper.use(
      OfflineProjectStorageService.offlineStorageMutex,
      async () => {
        const expiredItems =
          this.offlineStorageTimestampService.getExpiredItems(
            OfflineProjectStorageService.timestampCollectionKey
          );
        if (CollectionHelper.isPopulated(expiredItems)) {
          expiredItems.forEach((i) =>
            localStorage.removeItem(
              OfflineProjectStorageService.getProjectStorageKey(Number(i.key))
            )
          );
          this.offlineStorageTimestampService.removeItems(
            OfflineProjectStorageService.timestampCollectionKey,
            expiredItems.map((i) => i.key)
          );
        }
        setTimeout(
          () => this.removeExpiredItems(),
          this.expiredItemRemovalInterval
        );
      }
    );
  }
}
